import { useCallback, useEffect, useState } from "react";
import { client } from "../lib/api";
import { registerSessionSignOut } from "../lib/sessionExpiry";

/**
 * useMemberAuth — lightweight singleton hook for the member session.
 *
 * Stores the bearer token in localStorage and exposes `member`, helpers,
 * and an axios-injected getter. Auth is independent from admin auth.
 */

const TOKEN_KEY = "archive_member_token";
// Reuse the audience tracker's session id so the member inherits the same
// callsign that the Reach Globe / visitor dossier already shows.
const VISITOR_HASH_KEY = "archive_live_sid";

const _listeners = new Set();
let _cache = { member: null, loading: true, token: null };

const _emit = () => _listeners.forEach((fn) => fn(_cache));

const memberClient = () => {
  const t = localStorage.getItem(TOKEN_KEY) || null;
  if (!t) return null;
  return null;
};

const _fetchMe = async () => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) {
    _cache = { member: null, loading: false, token: null };
    _emit();
    return;
  }
  try {
    const r = await client.get("/members/me", { headers: { Authorization: `Bearer ${t}` } });
    _cache = { member: r.data.member, loading: false, token: t };
  } catch (e) {
    if (e?.response?.status === 401) localStorage.removeItem(TOKEN_KEY);
    _cache = { member: null, loading: false, token: null };
  }
  _emit();
};

// Kick off the initial fetch immediately so the first hook subscriber
// doesn't see a stale "loading" flicker.
let _bootstrapped = false;
const _bootstrap = () => {
  if (_bootstrapped) return;
  _bootstrapped = true;
  _fetchMe();
};

/**
 * Imperative entry-point used by the new /auth/* pages. Writes the bearer
 * to localStorage, refetches /me, and emits to all listeners so the SPA
 * picks up the new session WITHOUT a full reload.
 */
export const setMemberSession = async (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
  await _fetchMe();
};

// Global 401 handling (lib/sessionExpiry) signs the member out through here.
registerSessionSignOut(() => { setMemberSession(null); });

export const useMemberAuth = () => {
  const [snapshot, setSnapshot] = useState(_cache);
  useEffect(() => {
    // Register the listener FIRST so we never miss the bootstrap emit.
    _listeners.add(setSnapshot);
    _bootstrap();
    // If bootstrap already happened (other mount), pull the current cache.
    setSnapshot(_cache);
    return () => { _listeners.delete(setSnapshot); };
  }, []);

  const subscribe = useCallback(async (payload) => {
    const r = await client.post("/members/subscribe", {
      ...payload,
      visitor_hash: payload.visitor_hash
        || localStorage.getItem(VISITOR_HASH_KEY) || null,
    });
    const token = r.data.session_token;
    localStorage.setItem(TOKEN_KEY, token);
    _cache = { member: r.data.member, loading: false, token };
    _emit();
    return r.data.member;
  }, []);

  const patchMe = useCallback(async (payload) => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) throw new Error("Not signed in");
    const r = await client.patch("/members/me", payload, {
      headers: { Authorization: `Bearer ${t}` },
    });
    _cache = { ..._cache, member: r.data.member };
    _emit();
    return r.data.member;
  }, []);

  const fetchSuggestions = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return [];
    const r = await client.get("/members/suggestions", {
      headers: { Authorization: `Bearer ${t}` },
    });
    return r.data.suggestions || [];
  }, []);

  const logout = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    _cache = { member: null, loading: false, token: null };
    _emit();
    if (t) {
      // Prefer new auth-module logout (deletes session row); fall back to
      // legacy endpoint for older tokens.
      try {
        await client.post("/auth/member/logout", null, {
          headers: { Authorization: `Bearer ${t}` },
        });
      } catch {
        try { await client.post("/members/logout", null, {
          headers: { Authorization: `Bearer ${t}` },
        }); } catch { /* silent */ }
      }
    }
  }, []);

  return {
    member: snapshot.member,
    loading: snapshot.loading,
    isMember: !!snapshot.member,
    token: snapshot.token,
    subscribe,
    patchMe,
    fetchSuggestions,
    logout,
  };
};

export { memberClient };
